import { ApolloError, QueryOptions } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  HStack,
  IconButton,
  Popover,
  PopoverTrigger,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import AccountCircleSharpIcon from '@gamma/ui/icons/AccountCircleSharpIcon';
import ArrowForwardSharpIcon from '@gamma/ui/icons/ArrowForwardSharpIcon';
import CheckCircleSharpIcon from '@gamma/ui/icons/CheckCircleSharpIcon';
import ForwardSharpIcon from '@gamma/ui/icons/ForwardSharpIcon';
import NotificationsOffSharpIcon from '@gamma/ui/icons/NotificationsOffSharpIcon';
import NotificationsSharpIcon from '@gamma/ui/icons/NotificationsSharpIcon';
import VisibilitySharpIcon from '@gamma/ui/icons/VisibilitySharpIcon';
import { RouterLink } from '@gamma/ui/navigation';
import { LoadingSpinner } from '@gamma/ui/progress';
import { useEffect, useMemo, useState } from 'react';
import { IGetDetectionVariables } from '../..';
import { ROUTES } from '@investigator/shared/constants';
import { useQueryParams } from '@investigator/shared/hooks/useQueryParams';
import { i18n } from '@investigator/shared/localization';
import { UserRole } from '@investigator/shared/providers';
import { useQueryEscalationConfigurationsPublic } from '../../../../integrations/queries';
import { DetectionQueryVariables } from '../../../pages';
import { IQueryAlertMetadataExcludedEntitiesPaginated } from '../../../queries';

import {
  ServiceNameEnum,
  Detection,
} from '@investigator/shared/generated/graphql';
import {
  AssignUserContent,
  CloseDetectionContent,
  SendToContent,
  SuppressEntityContent,
} from '../PopoverContent';
import {
  getSourceEntityForSuppression,
  getEntityInstanceFromDetection,
} from '@investigator/shared/utils';
import { useEntitiesContext } from '@investigator/features/entities/context/EntitiesContext';

const { viewAlertCategory, viewDetection } = i18n.pages.detections;

const { excludeEntity } = i18n.pages.entityDetails;

const { detectionIsClosed, suppress, unSuppress } = excludeEntity;

const { close, controls, detectionIsSent, sendTo, SNConfigureAndEnable } =
  i18n.pages.detections;
const { entitySuppressTooltip } = i18n.pages.entities;

const { assignTo } = controls;

interface ActionsMenuProps {
  detection: Detection;
  isRelated?: boolean;
  refetchQueries: QueryOptions[];
  isRowActions?: boolean;
  isRowActionsExpanded?: boolean;
  excludedEntitiesData?: IQueryAlertMetadataExcludedEntitiesPaginated;
  excludedEntitiesError?: ApolloError;
  excludedEntitiesLoading: boolean;
  getDetectionVariables?: IGetDetectionVariables;
  queryDetectionsPaginatedVariables?: DetectionQueryVariables;
  userRole: UserRole;
}

export const ActionsMenu = ({
  detection,
  isRelated,
  isRowActions,
  isRowActionsExpanded,
  refetchQueries,
  excludedEntitiesData,
  excludedEntitiesLoading,
  getDetectionVariables,
  queryDetectionsPaginatedVariables,
  userRole,
}: ActionsMenuProps) => {
  const toast = useToast();
  const { setSelectedEntity } = useEntitiesContext();

  const sourceEntity = useMemo(
    () => getSourceEntityForSuppression(detection),
    [detection],
  );

  const [closeLoading, setCloseLoading] = useState<boolean>(false);
  const [assignLoading, setAssignLoading] = useState<boolean>(false);
  const [sendToLoading, setSendToLoading] = useState<boolean>(false);

  const [isClosed, setIsClosed] = useState<boolean>(false);
  const [isSuppressed, setIsSuppressed] = useState<boolean>(false);
  const [isSentTo, setIsSentTo] = useState<boolean>(false);

  const searchParams = useQueryParams();
  const detailsTo = useMemo(() => {
    const searchDetectionId = detection.detection_id;
    if (searchDetectionId) {
      searchParams.set('detection_id', searchDetectionId);
    }
    return {
      pathName: `${ROUTES.detections}/${detection.detection_id}/details`,
      search: searchParams.toString(),
    };
  }, [searchParams, detection]);

  const {
    isOpen: closeIsOpen,
    onClose: onCloseClose,
    onToggle: onToggleClose,
  } = useDisclosure();
  const {
    isOpen: suppressIsOpen,
    onClose: onCloseSuppress,
    onToggle: onToggleSuppress,
  } = useDisclosure();
  const {
    isOpen: sendToIsOpen,
    onClose: onCloseSendTo,
    onToggle: onToggleSendTo,
  } = useDisclosure();
  const {
    isOpen: assignIsOpen,
    onClose: onCloseAssign,
    onToggle: onToggleAssign,
  } = useDisclosure();

  const {
    loading: escalationConfigurationsLoading,
    data: escalationConfigurationsData,
  } = useQueryEscalationConfigurationsPublic({
    skip: !detection.tenant_info?.tenant_id,
    variables: {
      tenant: detection.tenant_info?.tenant_id,
    },
  });

  const serviceNowConfigurationRaw =
    escalationConfigurationsData?.queryEscalationConfigurations
      ?.escalation_configurations?.[0];
  const serviceNowConfiguration = serviceNowConfigurationRaw
    ? {
        ...serviceNowConfigurationRaw,
        service_name:
          serviceNowConfigurationRaw.service_name as ServiceNameEnum,
      }
    : undefined;

  useEffect(() => {
    setIsClosed(detection.detection_status !== 'open');
    setIsSentTo(!!detection.escalation_data?.escalation_status);
    if (excludedEntitiesData) {
      setIsSuppressed(
        excludedEntitiesData?.queryAlertMetadataExcludedEntitiesPaginated
          ?.excluded_entities?.length > 0,
      );
    }
  }, [detection, excludedEntitiesData]);

  const getSuppressTooltipLabel = () => {
    if (!sourceEntity) return entitySuppressTooltip;
    return isSuppressed ? unSuppress : suppress;
  };

  return (
    <HStack>
      <ButtonGroup
        data-testid={
          userRole !== 'viewer'
            ? 'detection-actions-menu'
            : 'detection-actions-menu-viewer'
        }
      >
        {userRole !== 'viewer' && (
          <>
            <Popover
              isOpen={closeIsOpen}
              onClose={onCloseClose}
              placement="bottom-start"
            >
              <PopoverTrigger>
                <span>
                  <Tooltip
                    placement="bottom-start"
                    shouldWrapChildren={true}
                    label={isClosed ? detectionIsClosed : close}
                  >
                    <IconButton
                      size="box-sm"
                      variant="solid"
                      colorScheme="gray"
                      aria-label="action"
                      onClick={() => {
                        onToggleClose();
                        toast.closeAll();
                      }}
                      isDisabled={isClosed || closeLoading}
                      data-testid="close-detection-trigger"
                      icon={
                        closeLoading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <CheckCircleSharpIcon />
                        )
                      }
                    />
                  </Tooltip>
                </span>
              </PopoverTrigger>
              <CloseDetectionContent
                onClose={onCloseClose}
                setIsClosed={setIsClosed}
                setIsLoading={setCloseLoading}
                refetchQueries={refetchQueries}
                refetchMain={isRelated && isRowActions}
                excludedEntitiesLoading={excludedEntitiesLoading}
              />
            </Popover>
            {!isRowActions || isRowActionsExpanded ? (
              <Popover
                isOpen={sendToIsOpen}
                onClose={onCloseSendTo}
                placement="bottom-start"
              >
                <PopoverTrigger>
                  <span>
                    <Tooltip
                      placement="bottom-start"
                      shouldWrapChildren={true}
                      label={
                        isSentTo
                          ? detectionIsSent
                          : !sendToLoading && !serviceNowConfiguration?.enabled
                            ? SNConfigureAndEnable
                            : sendTo
                      }
                    >
                      <IconButton
                        size="box-sm"
                        variant="solid"
                        colorScheme="gray"
                        aria-label="action"
                        onClick={() => {
                          onToggleSendTo();
                          toast.closeAll();
                        }}
                        isDisabled={
                          isSentTo ||
                          sendToLoading ||
                          !serviceNowConfiguration ||
                          !serviceNowConfiguration?.enabled
                        }
                        data-testid="send-to-trigger"
                        icon={
                          sendToLoading || escalationConfigurationsLoading ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            <ForwardSharpIcon />
                          )
                        }
                      />
                    </Tooltip>
                  </span>
                </PopoverTrigger>
                <SendToContent
                  onClose={onCloseSendTo}
                  setIsSentTo={setIsSentTo}
                  setIsLoading={setSendToLoading}
                  refetchQueries={refetchQueries}
                  refetchMain={isRelated && isRowActions}
                  serviceNowConfiguration={serviceNowConfiguration}
                  excludedEntitiesLoading={excludedEntitiesLoading}
                />
              </Popover>
            ) : null}
            <Popover
              isOpen={suppressIsOpen}
              onClose={onCloseSuppress}
              placement="bottom-start"
            >
              <PopoverTrigger>
                <span>
                  <Tooltip
                    placement="bottom-start"
                    shouldWrapChildren={true}
                    label={getSuppressTooltipLabel()}
                  >
                    <IconButton
                      size="box-sm"
                      variant="solid"
                      colorScheme="gray"
                      aria-label="action"
                      isDisabled={!sourceEntity}
                      onClick={() => {
                        // Map detection data to EntityInstance and set in context
                        if (sourceEntity && detection) {
                          const entityInstance = {
                            ...getEntityInstanceFromDetection(
                              detection,
                              sourceEntity,
                            ),
                            entityIsSuppressed: isSuppressed,
                          };
                          setSelectedEntity(entityInstance);
                        }
                        onToggleSuppress();
                        toast.closeAll();
                      }}
                      data-testid="suppress-entity-trigger"
                      icon={
                        excludedEntitiesLoading ? (
                          <LoadingSpinner size="sm" />
                        ) : !isSuppressed ? (
                          <NotificationsOffSharpIcon />
                        ) : (
                          <NotificationsSharpIcon />
                        )
                      }
                    />
                  </Tooltip>
                </span>
              </PopoverTrigger>
              <SuppressEntityContent
                onClose={onCloseSuppress}
                refetchMain={isRelated && isRowActions}
                excludedEntitiesLoading={excludedEntitiesLoading}
                isSuppressed={isSuppressed}
              />
            </Popover>
            <Popover
              isOpen={assignIsOpen}
              onClose={onCloseAssign}
              placement="bottom-start"
            >
              <PopoverTrigger>
                <span>
                  <Tooltip
                    placement="bottom-start"
                    shouldWrapChildren={true}
                    label={assignTo}
                  >
                    <IconButton
                      size="box-sm"
                      variant="solid"
                      colorScheme="gray"
                      aria-label="action"
                      onClick={() => {
                        onToggleAssign();
                        toast.closeAll();
                      }}
                      data-testid="assign-detection-trigger"
                      icon={
                        assignLoading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <AccountCircleSharpIcon />
                        )
                      }
                    />
                  </Tooltip>
                </span>
              </PopoverTrigger>
              <AssignUserContent
                detection={detection}
                onClose={onCloseAssign}
                setIsLoading={setAssignLoading}
                refetchMain={isRelated && isRowActions}
                getDetectionVariables={getDetectionVariables}
                queryDetectionsPaginatedVariables={
                  queryDetectionsPaginatedVariables
                }
              />
            </Popover>
          </>
        )}
        {isRowActions && !isRowActionsExpanded && (
          <Tooltip label={viewDetection} placement="bottom-start">
            <IconButton
              as={RouterLink}
              size={'box-sm'}
              variant="solid"
              colorScheme="blue"
              aria-label="action"
              data-testid="view-detection-button"
              to={`${detailsTo?.pathName}?${detailsTo?.search}`}
              icon={<VisibilitySharpIcon />}
            />
          </Tooltip>
        )}
        {isRowActionsExpanded && (
          <>
            {detection.alert_info?.content_id && (
              <Button
                size="sm"
                as={RouterLink}
                variant="solid"
                colorScheme="gray"
                data-testid="view-detection-alert-button"
                to={`${ROUTES.systemSettingsAlertCatalogId(
                  detection.alert_info.content_id,
                  `?${detailsTo.search}`,
                )}`}
              >
                {viewAlertCategory}
              </Button>
            )}
            <Button
              size="sm"
              as={RouterLink}
              variant="solid"
              colorScheme="blue"
              rightIcon={<ArrowForwardSharpIcon />}
              to={`${detailsTo.pathName}?${detailsTo.search}`}
            >
              {viewDetection}
            </Button>
          </>
        )}
      </ButtonGroup>
    </HStack>
  );
};
